import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';

interface TimeframePanelProps {
  selectedTimeframe: string;
  onTimeframeChange: (timeframe: string) => void;
  selectedChartType: string;
  onChartTypeChange: (chartType: string) => void;
  isRightPanelVisible?: boolean;
}

const TimeframePanel: React.FC<TimeframePanelProps> = ({
  selectedTimeframe,
  onTimeframeChange,
  selectedChartType,
  onChartTypeChange,
  isRightPanelVisible = false,
}) => {
  const { theme } = useTheme();
  const [timeframeIndex, setTimeframeIndex] = useState(0);
  const [showArrows, setShowArrows] = useState(false);

  const timeframes = ['1m', '5m', '15m', '30m', '1H', '4H', '6H', '12H', '1D', '1W', '3W', '1M', '2M', '3M', '6M', '1Y'];
  const chartTypes = ['line', 'area', 'candlestick'];

  // Handle responsive timeframe display and arrow navigation
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      
      // If right panel is visible, override width-based logic
      if (isRightPanelVisible) {
        setShowArrows(true);
        // Always show 5 timeframes when right panel is visible
        const visibleLength = 6;
        const maxIndex = Math.max(0, timeframes.length - visibleLength);
        if (timeframeIndex > maxIndex) {
          setTimeframeIndex(maxIndex);
        }
        return; // Exit early, don't apply width-based logic
      }
      
      // Normal width-based logic (only when right panel is not visible)
      if (width < 660) {
        setShowArrows(true);
        const maxIndex = Math.max(0, timeframes.length - 3);
        if (timeframeIndex > maxIndex) {
          setTimeframeIndex(maxIndex);
        }
      } else if (width >= 660 && width < 1200) {
        setShowArrows(true);
        const visibleLength = 8;
        const maxIndex = Math.max(0, timeframes.length - visibleLength);
        if (timeframeIndex > maxIndex) {
          setTimeframeIndex(maxIndex);
        }
      } else if (width >= 1200 && width < 1400) {
        setShowArrows(true);
        const maxIndex = Math.max(0, timeframes.length - 10);
        if (timeframeIndex > maxIndex) {
          setTimeframeIndex(maxIndex);
        }
      } else {
        setShowArrows(false);
        setTimeframeIndex(0);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [timeframeIndex, timeframes.length, isRightPanelVisible]);

  const navigateTimeframe = (direction: 'left' | 'right') => {
    if (direction === 'left' && timeframeIndex > 0) {
      setTimeframeIndex(prev => prev - 1);
    } else if (direction === 'right') {
              // If right panel is visible, always use 5 timeframes
        if (isRightPanelVisible) {
          if (timeframeIndex < timeframes.length - 6) {
          setTimeframeIndex(prev => prev + 1);
        }
        return; // Exit early, don't apply width-based logic
      }
      
      // Normal width-based navigation logic
      const width = window.innerWidth;
      if (width < 660 && timeframeIndex < timeframes.length - 3) {
        setTimeframeIndex(prev => prev + 1);
      } else if (width >= 660 && width < 1200) {
        const visibleLength = 8;
        if (timeframeIndex < timeframes.length - visibleLength) {
          setTimeframeIndex(prev => prev + 1);
        }
      } else if (width >= 1200 && width < 1400 && timeframeIndex < timeframes.length - 10) {
        setTimeframeIndex(prev => prev + 1);
      }
    }
  };

  // Get visible timeframes based on current index
  const getVisibleTimeframes = () => {
    // If right panel is visible, always show 5 timeframes
    if (isRightPanelVisible) {
      return timeframes.slice(timeframeIndex, timeframeIndex + 6);
    }
    
    // Normal width-based logic (only when right panel is not visible)
    if (!showArrows) return timeframes;
    
    const width = window.innerWidth;
    if (width < 660) {
      return timeframes.slice(timeframeIndex, timeframeIndex + 3);
    } else if (width >= 660 && width < 1200) {
      return timeframes.slice(timeframeIndex, timeframeIndex + 8);
    } else if (width >= 1200 && width < 1400) {
      return timeframes.slice(timeframeIndex, timeframeIndex + 10);
    }
    return timeframes;
  };

  return (
    <div className="flex items-center space-x-3 mt-2 ">
      {/* Timeframe Selector */}
      <div className="flex items-center space-x-2">
        {/* Left Arrow - Only show when not at beginning and arrows are enabled */}
        {showArrows && timeframeIndex > 0 && (
          <button
            onClick={() => navigateTimeframe('left')}
            className="p-1 text-xs theme-text-quaternary hover:theme-text-primary transition-colors duration-200"
            title="Previous timeframes"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}

        {/* Timeframe Buttons */}
        <div className="flex rounded-md border theme-border">
          {getVisibleTimeframes().map(tf => (
            <button
              key={tf}
              onClick={() => onTimeframeChange(tf)}
              className={`px-2 py-1 text-xs font-medium transition-colors duration-200 ${
                selectedTimeframe === tf
                  ? theme === 'light' ? 'bg-white text-gray-900' : 'bg-[#000000] text-white'
                  : `theme-text-quaternary ${
                      theme === 'light' 
                        ? 'hover:text-[#2AB7A6] hover:bg-gray-50' 
                        : 'hover:theme-text-primary hover:theme-bg-quaternary'
                    }`
              } ${tf === getVisibleTimeframes()[0] ? 'rounded-l-md' : ''} ${
                tf === getVisibleTimeframes()[getVisibleTimeframes().length - 1]
                  ? 'rounded-r-md'
                  : ''
              }`}
            >
              {tf}
            </button>
          ))}
        </div>

        {/* Right Arrow - Only show when not at end and arrows are enabled */}
        {showArrows && (
          (isRightPanelVisible && timeframeIndex < timeframes.length - 6) ||
          (!isRightPanelVisible && (
            (window.innerWidth < 660 && timeframeIndex < timeframes.length - 3) || 
            (window.innerWidth >= 660 && window.innerWidth < 1200 && timeframeIndex < timeframes.length - 8) || 
            (window.innerWidth >= 1200 && window.innerWidth < 1400 && timeframeIndex < timeframes.length - 10)
          ))
        ) && (
          <button
            onClick={() => navigateTimeframe('right')}
            className="p-1 text-xs theme-text-quaternary hover:theme-text-primary transition-colors duration-200"
            title="Next timeframes"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        )}
      </div>

      {/* Chart Type Selector */}
      <div className="flex rounded-md border theme-border">
       {chartTypes.map(type => (
         <button
           key={type}
           onClick={() => onChartTypeChange(type)}
           className={`px-2 py-1 text-xs font-medium transition-colors duration-200 ${
             selectedChartType === type
               ? theme === 'light' ? 'bg-white text-gray-900' : 'bg-[#000000] text-white'
               : 'theme-text-quaternary hover:theme-text-primary hover:theme-bg-quaternary'
           } ${type === chartTypes[0] ? 'rounded-l-md' : ''} ${
              type === chartTypes[chartTypes.length - 1]
                ? 'rounded-r-md'
                : ''
            }`}
         >
           {type.charAt(0).toUpperCase() + type.slice(1)}
         </button>
       ))}
      </div>

       {/* Show Order Book Panel Button */}
       <button
         type="button"
                   onClick={() => {
            // Method 1: Try to call the global function
            if ((window as any).toggleRightPanel) {
              (window as any).toggleRightPanel();
              return;
            }
            
            // Method 2: Try to dispatch a custom event
            try {
              window.dispatchEvent(new CustomEvent('toggleRightPanel'));
              return;
            } catch (error) {
              // Silent error handling
            }
            
            // Method 3: Try to find the function after a delay
            setTimeout(() => {
              if ((window as any).toggleRightPanel) {
                (window as any).toggleRightPanel();
              } else {
                // Method 4: Show user-friendly message
                alert('Right panel toggle is not available. Please navigate to the Trading page first, then return here.');
              }
            }, 500);
          }}
         className={`hidden px-3 py-1 rounded-md text-xs font-medium transition-colors duration-200 border ${
           theme === 'light' 
             ? 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200' 
             : 'theme-bg-quaternary theme-text-secondary theme-border hover:theme-bg-quinary hover:theme-text-primary'
         }`}
       >
         <span className="inline-block align-middle mr-2" style={{ width: 16, height: 16 }}>
           <svg fill="currentColor" viewBox="0 0 640 640" width="16" height="16" style={{ display: 'inline' }}>
             <path d="M128 160C128 107 171 64 224 64C277 64 320 107 320 160L320 324.7C349.5 351.1 368 389.4 368 432C368 511.5 303.5 576 224 576C144.5 576 80 511.5 80 432C80 389.4 98.5 351 128 324.7L128 160zM224 496C259.3 496 288 467.3 288 432C288 405.1 271.5 382.1 248 372.7L248 280C248 266.7 237.3 256 224 256C210.7 256 200 266.7 200 280L200 372.7C176.5 382.2 160 405.2 160 432C160 467.3 188.7 496 224 496zM416 96L512 96C529.7 96 544 110.3 544 128C544 145.7 529.7 160 512 160L416 160C398.3 160 384 145.7 384 128C384 110.3 398.3 96 416 96zM416 224L512 224C529.7 224 544 238.3 544 256C544 273.7 529.7 288 512 288L416 288C398.3 288 384 273.7 384 256C384 238.3 398.3 224 416 224zM416 384C416 366.3 430.3 352 448 352L512 352C529.7 352 544 366.3 544 384C544 401.7 529.7 416 512 416L448 416C430.3 416 416 401.7 416 384z"/>
           </svg>
         </span>
         Books
       </button>

    </div>
  );
};

export default TimeframePanel;
